import React, { useEffect, useCallback, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import PageHeader from '../../../components/layout/PageHeader';
import {
  Zap, TrendingUp, AlertTriangle, Activity, Star, FileText, MessageCircle,
  XCircle, Loader2, Filter, RefreshCw, ArrowUpRight, Shield, Eye,
} from 'lucide-react';
import ShareToChat from '../../../components/ui/ShareToChat';
import { useSignalStore } from '../../../state/signalStore';
import { useWatchlistStore } from '../../../state/watchlistStore';
import { useViewStore } from '../../../state/viewStore';
import { useAuthStore } from '../../../state/authStore';
import { Input } from '../../../components/ui/input';

const SIGNAL_TYPE_META = {
  insider_accumulation: { label: 'Insider Buying', icon: TrendingUp, color: 'text-emerald-500' },
  promoter_increase: { label: 'Promoter Increase', icon: Shield, color: 'text-blue-500' },
  fii_dii_convergence: { label: 'FII+DII Convergence', icon: Activity, color: 'text-purple-500' },
  valuation_compression: { label: 'Valuation Compression', icon: Zap, color: 'text-amber-500' },
  FII_THRESHOLD: { label: 'FII Threshold', icon: TrendingUp, color: 'text-blue-500' },
  DII_THRESHOLD: { label: 'DII Threshold', icon: TrendingUp, color: 'text-indigo-500' },
  INSIDER_BUY: { label: 'Insider Buy Alert', icon: TrendingUp, color: 'text-emerald-500' },
  INSIDER_SELL: { label: 'Insider Sell Alert', icon: AlertTriangle, color: 'text-red-400' },
  EARNINGS_DATE: { label: 'Earnings Date', icon: Activity, color: 'text-amber-500' },
  PROMOTER_PLEDGE: { label: 'Promoter Holding Drop', icon: AlertTriangle, color: 'text-red-400' },
  CORPORATE_ACTION: { label: 'Corporate Action', icon: FileText, color: 'text-blue-500' },
  SCORE_CHANGE: { label: 'Score Change', icon: Zap, color: 'text-purple-500' },
};

const SEVERITY_COLORS = {
  HIGH: 'bg-red-500/10 text-red-500 border-red-500/30',
  MEDIUM: 'bg-amber-500/10 text-amber-600 border-amber-500/30',
  LOW: 'bg-blue-500/10 text-blue-500 border-blue-500/30',
};

function SignalCard({ signal, onAction, onAssignClick }) {
  const meta = SIGNAL_TYPE_META[signal.type] || { label: signal.type, icon: Zap, color: 'text-muted-foreground' };
  const Icon = meta.icon;
  const sevColor = SEVERITY_COLORS[signal.severity] || SEVERITY_COLORS.MEDIUM;

  return (
    <Card className="hover:border-emerald-500/20 transition-colors">
      <CardContent className="pt-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 bg-card border ${meta.color}`}>
              <Icon className="w-4 h-4" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-semibold text-sm truncate">{signal.title}</h3>
                <Badge className={`text-[9px] ${sevColor}`}>{signal.severity}</Badge>
                <Badge className="text-[9px] bg-gray-500/10 text-gray-500">{meta.label}</Badge>
              </div>
              {signal.symbol && (
                <p className="text-xs text-emerald-500 font-medium mt-0.5">{signal.symbol} — {signal.company_name}</p>
              )}
              {signal.message && (
                <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{signal.message}</p>
              )}
              {signal.timestamp && (
                <p className="text-[10px] text-muted-foreground mt-1">
                  {new Date(signal.timestamp).toLocaleString()}
                </p>
              )}
              {signal.conviction > 0 && (
                <div className="flex items-center gap-1 mt-1">
                  <div className="h-1.5 w-16 bg-border rounded-full overflow-hidden">
                    <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${signal.conviction * 100}%` }} />
                  </div>
                  <span className="text-[10px] text-muted-foreground">{Math.round(signal.conviction * 100)}% conviction</span>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-1.5 mt-3 pt-3 border-t">
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => onAssignClick(signal)}>
            Assign
          </Button>
          <ShareToChat
            messageType="shared_signal"
            payload={{
              type: 'signal',
              title: signal.title,
              symbol: signal.symbol || '',
              signal_type: signal.signal_type || '',
              summary: signal.message || '',
            }}
          />
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => onAction(signal, 'watchlist')}>
            <Star className="w-3 h-3 mr-1" />Watchlist
          </Button>
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => onAction(signal, 'ai_chat')}>
            <MessageCircle className="w-3 h-3 mr-1" />Ask AI
          </Button>
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => onAction(signal, 'report')}>
            <FileText className="w-3 h-3 mr-1" />Report
          </Button>
          {signal.symbol && (
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => onAction(signal, 'view')}>
              <ArrowUpRight className="w-3 h-3 mr-1" />View
            </Button>
          )}
          <Button size="sm" variant="ghost" className="h-7 text-xs ml-auto text-muted-foreground" onClick={() => onAction(signal, 'dismiss')} aria-label="Dismiss signal">
            <XCircle className="w-3 h-3" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default function SignalCenterPage() {
  const navigate = useNavigate();
  const user = useAuthStore(s => s.user);
  const { loading, error, filters, fetchSignals, recordAction, setFilter, getFilteredSignals } = useSignalStore();
  const { addToWatchlist, isInWatchlist } = useWatchlistStore();
  const { viewChanges, fetchViewChanges } = useViewStore();

  const [assignSignal, setAssignSignal] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [orgId, setOrgId] = useState('');
  const [assignee, setAssignee] = useState('');
  const [taskNote, setTaskNote] = useState('');
  const [dueDate, setDueDate] = useState('');

  useEffect(() => { fetchSignals(); fetchViewChanges(7); }, [fetchSignals, fetchViewChanges]);

  useEffect(() => {
    if (!user?.id) return;
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/orgs`).then(({ data }) => {
      const o = Array.isArray(data) ? data : [];
      setOrgs(o);
      if (o[0]?.id) setOrgId(o[0].id);
    }).catch(() => {});
  }, [user?.id]);

  const filteredSignals = getFilteredSignals();

  const handleAction = useCallback((signal, actionType) => {
    if (actionType === 'view' && signal.symbol) {
      navigate(`/public/india-company/${signal.symbol}`);
      return;
    }
    if (actionType === 'ai_chat') {
      const prompt = encodeURIComponent(`Explain the ${signal.type?.replace(/_/g, ' ')} signal for ${signal.symbol || signal.company_name}. What does this mean for investors?`);
      navigate(`/public/ai?prompt=${prompt}`);
      recordAction(signal.id, signal.source, 'ai_chat');
      return;
    }
    if (actionType === 'report' && signal.symbol) {
      navigate(`/public/reports?isin=${signal.isin || ''}&playbook=initiation_note`);
      recordAction(signal.id, signal.source, 'report');
      return;
    }
    if (actionType === 'watchlist' && signal.symbol) {
      if (!isInWatchlist(signal.symbol)) {
        addToWatchlist({
          symbol: signal.symbol,
          companyName: signal.company_name,
          isin: signal.isin,
        });
      }
      recordAction(signal.id, signal.source, 'watchlist');
      return;
    }
    if (actionType === 'dismiss') {
      recordAction(signal.id, signal.source, 'dismiss');
    }
  }, [navigate, recordAction, addToWatchlist, isInWatchlist]);

  const submitAssign = async () => {
    if (!assignSignal || !orgId || !assignee.trim()) return;
    await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/signal-tasks`, {
      org_id: orgId,
      signal_id: String(assignSignal.id),
      signal_type: assignSignal.type,
      isin: assignSignal.isin || undefined,
      symbol: assignSignal.symbol,
      assigned_to: assignee.trim(),
      notes: taskNote || undefined,
      due_date: dueDate || undefined,
    });
    setAssignSignal(null);
    setTaskNote('');
    setDueDate('');
  };

  const signalTypes = [...new Set(filteredSignals.map(s => s.source))];
  const severities = [...new Set(filteredSignals.map(s => s.severity))];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <PageHeader title="Signal Center" subtitle="AI signals, alerts, and actions in one place" />
        <Button size="sm" variant="outline" onClick={() => fetchSignals()} disabled={loading} aria-label="Refresh signals">
          <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} />Refresh
        </Button>
      </div>

      {error && !loading && (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {error}
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-2">
        <Filter className="w-4 h-4 text-muted-foreground" />
        <select value={filters.type} onChange={e => setFilter('type', e.target.value)}
          className="rounded-md border bg-background px-2 py-1 text-xs">
          <option value="all">All Sources</option>
          <option value="ai_signal">AI Signals</option>
          <option value="alert_event">Alert Events</option>
        </select>
        <select value={filters.severity} onChange={e => setFilter('severity', e.target.value)}
          className="rounded-md border bg-background px-2 py-1 text-xs">
          <option value="all">All Severity</option>
          <option value="HIGH">High</option>
          <option value="MEDIUM">Medium</option>
          <option value="LOW">Low</option>
        </select>
        <Badge className="text-[10px] bg-gray-500/10 text-gray-500 ml-auto">
          {filteredSignals.length} signal{filteredSignals.length !== 1 ? 's' : ''}
        </Badge>
      </div>

      {/* Views Changed This Week */}
      {viewChanges.length > 0 && (
        <Card className="border-amber-500/20">
          <CardContent className="pt-4">
            <div className="flex items-center gap-2 mb-3">
              <Eye className="w-4 h-4 text-amber-500" />
              <h3 className="text-sm font-semibold">Views Changed This Week</h3>
              <Badge className="text-[9px] bg-amber-500/10 text-amber-600">{viewChanges.length}</Badge>
            </div>
            <div className="space-y-2">
              {viewChanges.slice(0, 5).map(vc => (
                <div key={vc.id} className="flex items-center gap-3 text-xs p-2 rounded-md bg-card border cursor-pointer hover:border-amber-500/30"
                  onClick={() => navigate(`/public/india-company/${vc.nse_symbol}`)}>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium">{vc.company_name || vc.nse_symbol}</p>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <Badge className={`text-[9px] ${
                        vc.prev_outlook === 'bullish' ? 'bg-emerald-500/10 text-emerald-600' :
                        vc.prev_outlook === 'bearish' ? 'bg-red-500/10 text-red-600' : 'bg-amber-500/10 text-amber-600'
                      }`}>{vc.prev_outlook}</Badge>
                      <span className="text-muted-foreground">→</span>
                      <Badge className={`text-[9px] ${
                        vc.outlook === 'bullish' ? 'bg-emerald-500/10 text-emerald-600' :
                        vc.outlook === 'bearish' ? 'bg-red-500/10 text-red-600' : 'bg-amber-500/10 text-amber-600'
                      }`}>{vc.outlook}</Badge>
                    </div>
                    {vc.change_narrative && <p className="text-[10px] text-muted-foreground mt-1 line-clamp-1">{vc.change_narrative}</p>}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Signal Feed */}
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-emerald-500" /></div>
      ) : filteredSignals.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground">
          <Zap className="w-10 h-10 mx-auto mb-3 opacity-40" />
          <p className="font-medium">No signals detected</p>
          <p className="text-sm mt-1">Signals appear when AI detects unusual patterns in market data, or when your alert rules fire.</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-3">
          {filteredSignals.map(s => (
            <SignalCard key={`${s.source}-${s.id}`} signal={s} onAction={handleAction} onAssignClick={setAssignSignal} />
          ))}
        </div>
      )}

      {assignSignal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <Card className="w-full max-w-md">
            <CardContent className="pt-4 space-y-3">
              <h3 className="text-sm font-semibold">Assign signal</h3>
              <p className="text-xs text-muted-foreground">{assignSignal.title}</p>
              <div>
                <label className="text-xs text-muted-foreground">Organization</label>
                <select className="w-full mt-1 rounded-md border bg-background px-2 py-1.5 text-sm" value={orgId} onChange={e => setOrgId(e.target.value)}>
                  {orgs.map(o => (
                    <option key={o.id} value={o.id}>{o.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Assignee (user id)</label>
                <Input className="mt-1" value={assignee} onChange={e => setAssignee(e.target.value)} placeholder="Team member id" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Due date</label>
                <Input className="mt-1" type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Notes</label>
                <Input className="mt-1" value={taskNote} onChange={e => setTaskNote(e.target.value)} placeholder="Optional" />
              </div>
              <div className="flex gap-2 justify-end pt-2">
                <Button variant="ghost" size="sm" type="button" onClick={() => setAssignSignal(null)}>Cancel</Button>
                <Button size="sm" type="button" className="bg-emerald-600" onClick={submitAssign}>Create task</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
